import * as THREE from 'three'
import { ProceduralCity } from './ProceduralCity'
import type { MunichBuildingType } from './munichNeighborhood'

const limestone='#e9ddc5', trim='#f3e9d5', stone='#b9b09c', roofTile='#a9634b', iron='#415b56', glass='#537d81'
/** Munich's ordinary streets: shared batched parts, genuinely different building volumes. */
export class MunichNeighborhood extends ProceduralCity {
  private mnGable:THREE.BufferGeometry
  private mnMansard=new THREE.CylinderGeometry(.63,1,1,4)
  private mnHip=new THREE.ConeGeometry(1,1,4)
  private mnPavilionRoof=new THREE.ConeGeometry(1,1,8)
  private mnBarrel=new THREE.CylinderGeometry(1,1,1,24,1,false,-Math.PI/2,Math.PI)
  constructor(){
    super()
    const s=new THREE.Shape();s.moveTo(-.5,0);s.lineTo(.5,0);s.lineTo(0,1);s.closePath()
    this.mnGable=new THREE.ExtrudeGeometry(s,{depth:1,bevelEnabled:false});this.mnGable.translate(0,0,-.5)
    this.mnBarrel.rotateX(-Math.PI/2)
    ;[this.mnGable,this.mnMansard,this.mnHip,this.mnBarrel,this.mnPavilionRoof].forEach(g=>this.geometries.add(g))
  }
  protected mnRoof(x:number,y:number,z:number,w:number,d:number,h:number,color=roofTile){
    this.add(this.mnGable,this.mat(color),x,y,z,w,h,d)
    this.box(x,y+h+.035,z,.1,.1,d+.07,color)
    for(const side of [-1,1])this.box(x+side*w/2,y,z,.1,.12,d+.06,stone)
    for(let p=-d/2+.3;p<d/2;p+=.55)for(const side of [-1,1])
      this.beam(new THREE.Vector3(x+side*w/2,y+.02,z+p),new THREE.Vector3(x,y+h+.02,z+p),.014,'#c28a6a')
  }
  protected mnWindow(x:number,y:number,z:number,w=.65,h=.92,shutter=false){
    this.box(x,y,z,w+.18,h+.18,.08,trim)
    this.box(x,y,z+.055,w,h,.045,glass)
    this.box(x,y,z+.09,.045,h,.04,trim);this.box(x,y+.1,z+.09,w,.04,.04,trim)
    this.box(x,y-h/2-.07,z+.13,w+.25,.1,.22,limestone)
    if(shutter)for(const s of [-1,1]){
      this.box(x+s*(w/2+.23),y,z+.08,.26,h,.055,'#658170')
      for(let a=-h/2+.12;a<h/2;a+=.16)this.box(x+s*(w/2+.23),y+a,z+.12,.24,.027,.025,'#8a9c80')
    }
  }
  private mnWalls(w:number,d:number,h:number,color:string,floors:number,shutters=false){
    this.box(0,h/2+.35,0,w,h,d,color)
    this.box(0,.59,0,w+.06,.45,d+.06,stone)
    this.box(0,2.06,0,w+.16,.17,d+.16,limestone)
    this.box(0,h+.35,0,w+.26,.22,d+.26,trim)
    for(let side=0;side<4;side++)this.at(0,0,1,side*Math.PI/2,()=>{
      const width=side%2?d:w,front=(side%2?w:d)/2+.045
      const columns=width>4.2?3:2
      for(let floor=0;floor<floors;floor++)for(let c=0;c<columns;c++){
        const x=(c-(columns-1)/2)*(width/(columns+.4)),y=2.8+floor*1.35
        if(y+.5<h)this.mnWindow(x,y,front,.6,.9,shutters&&side%2===0)
      }
      for(const x of [-width/2+.13,width/2-.13]){
        this.box(x,h/2+.5,front,.2,h-.3,.12,limestone)
        for(let y=.85;y<h;y+=.48)this.box(x,y,front+.05,.32,.17,.12,trim)
      }
      // Recessed ground-floor openings also give the side/rear walls depth.
      for(const x of [-width*.27,width*.27])this.mnWindow(x,1.23,front,.74,1.03)
    })
  }
  private mnShop(w:number,d:number,name:string,color:string,awning=true){
    const z=d/2+.14
    this.box(0,1.11,z,w-.5,1.36,.09,iron)
    for(const x of [-w*.27,w*.27]){
      this.box(x,1.15,z+.06,w*.29,1.05,.04,'#8db0aa')
      this.box(x,.56,z+.08,w*.31,.13,.09,limestone)
    }
    this.box(0,1.07,z+.07,.61,1.23,.04,'#a8bbb0');this.disc(.2,1.03,z+.12,.025,.17,trim)
    this.sign(name,0,1.97,z+.09,w-.35,.32,'#eddbb2')
    if(awning){
      this.box(0,1.74,z+.29,w-.25,.09,.58,color)
      for(let x=-w/2+.22;x<w/2-.15;x+=.39)this.box(x,1.8,z+.29,.16,.035,.56,trim)
      this.box(0,1.65,z+.58,w-.25,.2,.04,color)
    }
  }
  private mnBalcony(x:number,y:number,z:number,w:number){
    this.box(x,y,z+.27,w,.12,.62,limestone)
    for(let a=-w/2+.06;a<w/2;a+=.19)this.box(x+a,y+.32,z+.56,.025,.62,.03,iron)
    this.box(x,y+.64,z+.56,w,.05,.05,iron)
    for(const s of [-1,1])this.box(x+s*w/2,y+.64,z+.28,.035,.04,.55,iron)
  }
  private mnChimney(x:number,y:number,z:number){
    this.box(x,y+.5,z,.35,1,.43,'#bd947e');this.box(x,y+1,z,.49,.12,.56,limestone)
    this.disc(x,y+1.18,z,.095,.28,roofTile)
  }
  private mnPlanter(x:number,z:number){
    this.box(x,.48,z,.58,.36,.52,'#a48b70');this.ball(x,.83,z,.36,'#6e8862',.8)
  }
  protected mnArch(x:number,y:number,z:number,w:number,h:number){
    const r=w/2,spring=y+h-r
    for(const s of [-1,1])this.box(x+s*(r+.06),(y+spring)/2,z,.12,spring-y,.18,limestone)
    for(let i=0;i<12;i++){
      const a=i*Math.PI/12,b=(i+1)*Math.PI/12
      this.beam(new THREE.Vector3(x+Math.cos(a)*(r+.06),spring+Math.sin(a)*(r+.06),z),new THREE.Vector3(x+Math.cos(b)*(r+.06),spring+Math.sin(b)*(r+.06),z),.065,limestone)
    }
  }
  private mnRail(x:number,y:number,z:number,w:number){
    this.box(x,y+.55,z,w,.06,.055,iron)
    for(let p=-w/2;p<=w/2;p+=.22)this.box(x+p,y+.27,z,.025,.54,.035,iron)
  }
  protected munichBuilding(kind:MunichBuildingType,seed:number,level=0,label?:string){
    const color=['#dcbfa2','#d5d9c1','#efd5ae','#d4b4a9','#d3c9b0'][seed%5]
    const extra=(seed%3)*.23+Math.min(level,5)*.13
    this.box(0,.23,0,5.6,.2,5.8,'#c6bea9')
    if(kind==='merchant'){
      // Two narrow merchant houses, different heights and stepped gable silhouettes.
      for(const side of [-1,1])this.at(side*1.16,0,1,0,()=>{
        const h=(side<0?5.8:4.55)+extra,w=2.12,d=4.15
        this.mnWalls(w,d,h,side<0?color:'#dab98c',side<0?3:2)
        this.mnRoof(0,h+.46,0,w+.15,d+.18,1.55)
        for(let i=0;i<4;i++)this.box(0,h+.44+i*.34,d/2+.15,w-i*.48,.37,.22,side<0?color:'#dab98c')
        this.mnWindow(0,h+.91,d/2+.28,.36,.55)
        this.mnShop(w,d,side<0?(label??'BÄCKEREI'):'BLUMEN',side<0?'#718875':'#b38169')
        this.mnChimney(-.55,h+.85,-1.2)
      })
    }else if(kind==='corner'){
      const h=7+extra;this.mnWalls(4.3,4.05,h,color,4)
      this.add(this.mnMansard,this.mat('#687773'),0,h+1.12,0,3.22,1.4,3.03,Math.PI/4)
      // An occupied corner bay rises into its own copper turret.
      this.disc(1.6,h/2+1,1.5,.88,h-.5,color)
      for(let y=2.9;y<h;y+=1.35){this.disc(1.6,y-.65,1.5,.93,.12,limestone)
        for(let f=0;f<5;f++)this.at(1.6,1.5,1,(f-2)*Math.PI/4,()=>this.mnWindow(0,y,.9,.38,.88))}
      this.disc(1.6,h+.75,1.5,.98,.22,trim)
      this.add(this.cone,this.mat('#587f71'),1.6,h+1.58,1.5,1.15,1.6,1.15,Math.PI/4)
      this.mnShop(4.15,4.05,label??'KAFFEEHAUS','#5c7d72',false)
      for(const y of [2.28,3.63,4.98])this.mnBalcony(-1.1,y,2.09,1.38)
    }else if(kind==='courtyard'){
      // An actual open U-shaped courtyard, rather than a painted recess on a cube.
      const h=5.4+extra
      this.box(0,h/2+.35,-1.55,4.65,h,1.3,color)
      for(const side of [-1,1]){
        this.box(side*1.74,h/2+.35,.32,1.18,h,3.75,color)
        this.mnRoof(side*1.74,h+.38,.25,1.4,3.9,.85)
        this.at(side*1.12,.15,1,-side*Math.PI/2,()=>{
          for(const x of [-1,0,1])for(let y=1.2;y<h;y+=1.35)this.mnWindow(x,y,0,.5,.83)
          for(const y of [2.08,3.43,4.78])this.mnBalcony(0,y,.1,2.9)
        })
        for(let y=1.3;y<h;y+=1.35){this.mnWindow(side*1.74,y,2.23,.57,.87,true)
          this.at(side*2.35,.2,1,side*Math.PI/2,()=>{for(const x of [-1,0,1])this.mnWindow(x,y,0,.5,.85)})}
      }
      this.at(0,-1.55,1,Math.PI/2,()=>this.mnRoof(0,h+.38,0,1.6,4.9,.85))
      this.box(0,.36,.4,2.15,.05,3.2,'#a99f86');this.mnPlanter(0,.5)
      this.box(0,1.15,-.86,.8,1.6,.08,iron)
      this.sign(label??'ISARHOF',0,.77,2.48,1.7,.3,'#e0d4ae')
      for(const x of [-.86,.86])this.mnPlanter(x,1.9)
    }else if(kind==='market'){
      const h=2.45+extra;this.mnWalls(4.8,4.3,h,'#ccae83',0)
      // Three glazed pitched roofs with exposed structural ribs.
      for(const x of [-1.6,0,1.6]){
        this.mnRoof(x,h+.38,0,1.58,4.6,1.3,'#81a7a2')
        for(const z of [-2.3,-1.15,0,1.15,2.3])for(const s of [-1,1])this.beam(new THREE.Vector3(x+s*.79,h+.41,z),new THREE.Vector3(x,h+1.72,z),.035,trim)
      }
      this.mnShop(4.75,4.3,label??'MARKTHALLE','#aa765b')
      for(const x of [-1.5,1.5]){this.box(x,.69,2.63,.84,.45,.43,'#a4845b');for(let j=0;j<3;j++)this.ball(x-.25+j*.25,.96,2.63,.1,j%2?'#ba8163':'#9daa6d')}
    }else if(kind==='gallery'){
      const h=4.8+extra;this.mnWalls(4.65,3.8,h,'#ddceb3',2)
      this.add(this.mnHip,this.mat('#748076'),0,h+1.04,-.1,3.47,1.35,2.9,Math.PI/4)
      this.box(0,1.2,2,2.7,1.7,.06,iron)
      for(const x of [-1.65,-.55,.55,1.65]){
        this.disc(x,1.77,2.37,.13,2.9,trim);this.box(x,.37,2.37,.42,.22,.42,stone);this.box(x,3.23,2.37,.42,.2,.42,trim)
      }
      this.box(0,3.42,2.35,4.1,.2,.63,limestone)
      this.add(this.mnGable,this.mat(limestone),0,3.5,2.35,4.25,1.1,.63)
      this.sign(label??'GALERIE',0,3.39,2.7,2.6,.24,'#e8d8ab')
      for(const x of [-1.5,1.5]){this.box(x,1.4,2.04,.53,1.1,.08,'#aa7365');this.box(x,1.55,2.1,.22,.55,.02,trim)}
    }else if(kind==='mansion'){
      const h=6.1+extra;this.mnWalls(4.35,4.05,h,color,3)
      this.add(this.mnMansard,this.mat('#62736f'),0,h+1.08,0,3.29,1.35,3.05,Math.PI/4)
      // Projecting central bay, stacked balconies, and dormers distinguish the villa.
      this.box(0,4.25,2.17,1.55,3.9,.65,color)
      for(const y of [3,4.35,5.7]){this.mnWindow(0,y,2.53,.85,.95);this.mnBalcony(0,y-.57,2.13,1.7)}
      this.add(this.mnGable,this.mat(trim),0,6.25,2.22,1.9,.9,.8)
      for(const x of [-1.36,1.36]){this.box(x,h+.9,1.7,.68,.85,.6,limestone);this.mnWindow(x,h+.96,2.04,.4,.56);this.mnRoof(x,h+1.34,1.7,.83,.8,.43,'#62736f')}
      this.mnShop(4.2,4.05,label??'ATELIER','#8e706c',false)
      this.mnChimney(-1.4,h+1.3,-1.2)
    }else if(kind==='workshop'){
      const h=3.4+extra;this.mnWalls(4.65,4.3,h,'#b67b61',1)
      for(let y=.9;y<h;y+=.35)for(const side of [-1,1])this.box(side*2.35,y,0,.03,.022,4.3,'#d6ad8c')
      for(const x of [-1.55,0,1.55]){
        this.mnRoof(x,h+.38,0,1.5,4.6,1.2,'#748477')
        this.box(x,h+.9,2.32,1.03,.8,.05,glass)
      }
      this.mnShop(4.55,4.3,label??'WERKSTATT','#9f745c',false)
      this.box(-1.65,h/2+1,-1.55,.57,h+1.2,.65,'#ab735b');this.mnChimney(-1.65,h+1.35,-1.55)
      this.box(1.55,.55,2.56,.85,.4,.46,stone)
    }else if(kind==='modern'){
      // Contemporary infill: stepped volumes, loggias, metal fins and planted terraces.
      const h=5.8+extra
      this.box(0,h/2+.35,-.15,4.65,h,4.15,'#d3d0ba')
      for(let f=0;f<4;f++){
        const y=.92+f*1.3
        for(let face=0;face<4;face++)this.at(0,-.15,1,face*Math.PI/2,()=>{
          const w=face%2?4.15:4.65,z=(face%2?4.65:4.15)/2+.035
          this.box(0,y+.32,z,w-.45,.9,.05,'#759b9b')
          for(let x=-w/2+.2;x<w/2;x+=.5)this.box(x,y+.32,z+.14,.045,1.1,.3,'#ab9877')
          this.box(0,y+.94,z,w+.12,.16,.23,limestone)
        })
      }
      this.box(-.5,h+.88,-.5,3.35,1.15,2.5,iron);this.box(-.5,h+1,-.5,3.4,.07,2.55,'#8ba8a1')
      this.box(0,h+.47,1.57,4.6,.2,.56,limestone)
      for(const x of [-1.7,-.5,.7,1.7]){this.box(x,h+.65,1.57,.65,.32,.47,'#a69578');this.ball(x,h+.94,1.57,.3,'#798d60',.7)}
      this.sign(label??'ISAR STUDIO',0,1.94,2.18,3.3,.27,'#e5d8b6')
    }else if(kind==='inn'){
      // Low Bavarian inn: timber galleries and a tall cross-gable roof.
      const h=3.8+extra;this.mnWalls(4.25,3.8,h,'#efdab4',1,true)
      this.mnRoof(0,h+.42,0,4.75,4.25,2.5)
      this.at(0,1.18,1,Math.PI/2,()=>this.mnRoof(0,h+.44,0,2.55,2.6,1.65))
      for(const face of [-1,1]){
        this.box(0,2.27,face*2.18,4.4,.16,.64,'#89694d')
        this.mnRail(0,2.37,face*2.48,4.25)
        for(const x of [-1.95,0,1.95])this.box(x,1.3,face*2.38,.14,1.9,.14,'#89694d')
      }
      this.box(0,h+1.06,2.03,2.15,1.3,.08,'#efdab4')
      this.mnWindow(0,h+1.09,2.09,.6,.8,true)
      for(const x of [-.9,.9])this.beam(new THREE.Vector3(x,h+.46,2.14),new THREE.Vector3(0,h+1.75,2.14),.055,'#89694d')
      this.mnShop(4.05,3.7,label??'GASTHAUS','#9c754e',false)
      this.mnChimney(-1.6,h+1.5,-.8)
      for(const x of [-1.5,1.5]){this.box(x,.78,2.63,.7,.08,.43,'#a5865c');this.box(x,.51,2.63,.12,.5,.12,iron)}
    }else if(kind==='cinema'){
      // A broad low auditorium and a narrow stepped tower with a vertical marquee.
      const h=3.25+extra;this.mnWalls(4.7,4.25,h,'#c9b9a4',1)
      this.box(0,h+.5,-.2,4.9,.18,4.1,limestone)
      for(let i=0;i<3;i++)this.box(-.6,h+1.15+i*.73,-.25,2.8-i*.62,1.5,3.3-i*.62,'#d9c9ad')
      for(const x of [-1.42,-1.06,-.7,-.34,.02,.38])this.box(x,h+1.15,1.44,.085,2.7,.09,limestone)
      this.box(1.32,4.5,2.2,.77,5.25,.4,'#a76b5f')
      for(const [i,char] of ['K','I','N','O'].entries())this.sign(char,1.32,6.02-i*.86,2.44,.59,.64,'#efd3a0')
      this.mnShop(4.65,4.25,label??'LICHTSPIEL','#a76b5f',false)
      this.box(0,2.14,2.38,4.7,.25,.78,'#a76b5f')
      for(let x=-2.1;x<=2.1;x+=.3)this.ball(x,2.1,2.79,.04,'#f3d898')
      for(const x of [-1.7,1.7]){this.box(x,1.18,2.29,.58,1.2,.07,iron);this.box(x,1.23,2.34,.39,.9,.025,'#c59a67')}
    }else if(kind==='arcade'){
      // Upper offices bridge an open, walkable ground-floor shopping arcade.
      const h=5.25+extra
      this.box(0,(h+2.35)/2,0,4.7,h-2.35,4.1,color)
      this.box(0,1.25,-1.7,4.7,1.85,.65,color)
      for(const x of [-2.1,-.7,.7,2.1]){
        this.box(x,1.34,1.93,.26,2.02,.36,limestone);this.box(x,.42,1.93,.43,.18,.55,stone)
        this.box(x,2.3,1.93,.42,.18,.56,limestone)
      }
      for(const x of [-1.4,0,1.4]){this.mnArch(x,.38,2.07,1.04,1.92);this.mnWindow(x,1.3,-1.33,.83,1.1)}
      for(let face=0;face<4;face++)this.at(0,0,1,face*Math.PI/2,()=>{
        const w=face%2?4.1:4.7,z=(face%2?4.7:4.1)/2+.04
        for(const y of [3.13,4.5])for(const x of [-w*.3,0,w*.3])this.mnWindow(x,y,z,.63,.9)
        this.box(0,2.44,z,w+.2,.24,.23,trim);this.box(0,h+.1,z,w+.3,.22,.26,trim)
      })
      this.mnRoof(0,h+.25,0,4.95,4.4,1.2,'#6f8075')
      this.sign(label??'PASSAGE',0,2.45,2.27,3.65,.25,'#ead6ad')
      this.box(0,.35,.8,4.1,.04,2.4,'#afa68f')
    }else if(kind==='conservatory'){
      // A transparent-looking barrel-vaulted winter garden, not a pitched market roof.
      const h=1.9+extra*.35,ry=1.65
      this.box(0,1.05,0,4.75,1.45,4.3,'#82a9a4')
      this.box(0,.48,0,4.85,.32,4.4,limestone)
      this.add(this.mnBarrel,this.mat('#91b7ae',.22),0,h,0,2.38,ry,4.3)
      for(let z=-2.18;z<=2.2;z+=.73){
        for(const s of [-1,1])this.box(s*2.38,(h+.45)/2,z,.075,h-.45,.08,limestone)
        for(let i=0;i<12;i++){const a=i*Math.PI/12,b=(i+1)*Math.PI/12;this.beam(new THREE.Vector3(Math.cos(a)*2.41,h+Math.sin(a)*ry*1.02,z),new THREE.Vector3(Math.cos(b)*2.41,h+Math.sin(b)*ry*1.02,z),.033,limestone)}
      }
      for(let i=1;i<6;i++){const a=i*Math.PI/6;this.box(Math.cos(a)*2.4,h+Math.sin(a)*ry+.025,0,.055,.055,4.4,limestone)}
      for(const side of [-1,1]){
        for(const x of [-1.6,-.8,0,.8,1.6]){
          const top=h+ry*Math.sqrt(1-(x/2.38)**2)
          this.box(x,(top+.48)/2,side*2.19,.055,top-.48,.065,limestone)
        }
        this.box(0,1.5,side*2.19,4.7,.055,.065,limestone)
        this.box(0,h+.68,side*2.19,4.3,.055,.065,limestone)
      }
      this.box(0,1.07,2.2,1,1.35,.1,iron);this.box(0,1.1,2.27,.76,1.15,.035,'#a9c6b8')
      this.sign(label??'WINTERGARTEN',0,2.02,2.24,3.3,.28,'#f0dfb7')
      for(const x of [-1.55,1.55]){this.mnPlanter(x,2.5);this.ball(x,1.12,2.5,.22,'#7b9470')}
    }else if(kind==='clockhouse'){
      // Asymmetrical municipal building with a slender clock tower and low wing.
      this.at(.72,-.12,1,0,()=>{this.mnWalls(3.05,3.85,3.6+extra,'#bd8c74',1);this.mnRoof(0,4.02+extra,0,3.3,4.15,1.45)})
      const tx=-1.65,tz=1.1,h=6.5+extra
      this.box(tx,h/2+.35,tz,1.62,h,1.72,'#c29a7e')
      for(const y of [.55,2.4,4.4,h+.35])this.box(tx,y,tz,1.8,.17,1.9,limestone)
      for(let face=0;face<4;face++)this.at(tx,tz,1,face*Math.PI/2,()=>{
        for(const y of [1.42,3.17])this.mnWindow(0,y,.89,.45,.8)
        const q=new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1,0,0),Math.PI/2)
        this.add(this.cylinder,this.mat(limestone),0,h-1,.92,.54,.055,.54,0,q)
        this.add(this.cylinder,this.mat(iron),0,h-1,.96,.45,.04,.45,0,q)
        this.beam(new THREE.Vector3(0,h-1,1),new THREE.Vector3(0,h-.7,1),.018,trim)
        this.beam(new THREE.Vector3(0,h-1,1),new THREE.Vector3(.26,h-1.1,1),.02,trim)
        for(const dx of [-.29,.29])this.mnWindow(dx,h-.07,.9,.21,.38)
      })
      this.add(this.mnHip,this.mat('#567b6b'),tx,h+1.07,tz,1.45,1.35,1.5,Math.PI/4)
      this.disc(tx,h+1.96,tz,.035,.45,'#bba576')
      this.sign(label??'BÜRGERHAUS',.7,2.12,1.96,2.5,.3,'#ecd7ad')
    }else if(kind==='terraces'){
      // Three genuine setbacks, with south-facing planted terraces and open railings.
      for(let f=0;f<3;f++){
        const d=4.5-f*1.05,z=-f*.525,y=.35+f*1.75,h=1.75+(f===2?extra:0),front=z+d/2
        this.box(0,y+h/2,z,4.45,h,d,'#e0d3bd');this.box(0,y+h+.02,z,4.66,.16,d+.16,limestone)
        for(const x of [-1.5,-.5,.5,1.5]){this.box(x,y+.87,front+.055,.75,1.13,.045,glass);this.box(x,y+.87,front+.095,.04,1.13,.04,trim)}
        for(const side of [-1,1])this.at(side*2.26,z,1,side*Math.PI/2,()=>{for(let x=-d/2+.55;x<d/2-.25;x+=.92)this.mnWindow(x,y+.92,0,.55,1)})
        this.at(0,z-d/2-.04,1,Math.PI,()=>{for(const x of [-1.5,-.5,.5,1.5])this.mnWindow(x,y+.92,0,.55,1)})
        if(f<2){this.mnRail(0,y+h+.12,front-.07,4.3);for(const x of [-1.67,1.67]){this.box(x,y+h+.25,front-.47,.7,.3,.48,'#a99072');this.ball(x,y+h+.5,front-.47,.3,'#73906f',.7)}}
      }
      this.sign(label??'GARTENHAUS',0,1.94,2.37,3.2,.28,'#ebdbb9')
      this.box(0,5.71+extra,-1.05,4.2,.08,2.24,'#88a080')
    }else if(kind==='rotunda'){
      // Octagonal café pavilion with wraparound glazing and a copper lantern roof.
      const h=2.7+extra*.3
      this.disc(0,.51,0,2.25,.38,limestone)
      const octagon=this.mnOctagon()
      this.add(octagon,this.mat('#d8c1a2'),0,h/2+.5,0,2.05,h,2.05)
      for(let face=0;face<8;face++)this.at(0,0,1,face*Math.PI/4+Math.PI/8,()=>{
        this.box(0,1.79,1.92,1.15,1.72,.045,glass)
        for(const x of [-.65,.65])this.box(x,1.76,1.97,.12,2.45,.14,trim)
        this.box(0,1.82,1.98,1.2,.045,.035,trim)
      })
      this.disc(0,h+.55,0,2.4,.18,limestone)
      this.add(this.mnPavilionRoof,this.mat('#6e9484'),0,h+1.32,0,2.55,1.4,2.55)
      this.disc(0,h+2.07,0,.35,.32,limestone);this.ball(0,h+2.34,0,.22,'#719586',.9)
      this.sign(label??'PAVILLON',0,2.82,2.16,2,.3,'#f0dcb4')
      this.mnPlanter(-1.8,2.24);this.mnPlanter(1.8,2.24)
    }else if(kind==='loft'){
      // A converted brick warehouse: tall multi-pane windows and a rooftop studio.
      const h=6+extra;this.box(0,h/2+.35,0,4.4,h,4.2,'#a8755e')
      for(let face=0;face<4;face++)this.at(0,0,1,face*Math.PI/2,()=>{
        const w=face%2?4.2:4.4,z=(face%2?4.4:4.2)/2+.04
        for(let y=.6;y<h;y+=.34)this.box(0,y,z,w,.025,.025,'#c6987d')
        for(const x of [-1.42,0,1.42])for(const y of [1.38,3.32,5.22]){
          this.mnWindow(x,y,z+.04,.93,1.38)
          for(const dy of [-.36,.36])this.box(x,y+dy,z+.15,.93,.035,.035,iron)
          this.box(x,y+.81,z+.08,1.1,.16,.13,'#c7987e')
        }
        for(const x of [-w/2+.12,w/2-.12])this.box(x,h/2+.45,z+.09,.25,h,.22,'#b8866c')
        this.box(0,h+.4,z,w+.24,.32,.32,limestone)
      })
      this.box(0,h+.51,0,4.48,.12,4.3,'#70857b')
      this.box(-.38,h+1.13,-.48,3,1.14,2.65,'#739e9c')
      for(let face=0;face<4;face++)this.at(-.38,-.48,1,face*Math.PI/2,()=>{
        const w=face%2?2.65:3,z=(face%2?3:2.65)/2+.035
        for(let x=-w/2+.12;x<w/2;x+=.5)this.box(x,h+1.13,z,.06,1.2,.1,iron)
      })
      this.mnRoof(-.38,h+1.7,-.48,3.2,2.85,.55,'#607e75')
      this.sign(label??'LOFTWERK',0,2.38,2.36,3.2,.27,'#eddbb3')
    }
  }
  private mnOctagonalBody?:THREE.BufferGeometry
  private mnOctagon(){
    if(!this.mnOctagonalBody){this.mnOctagonalBody=new THREE.CylinderGeometry(1,1,1,8);this.geometries.add(this.mnOctagonalBody)}
    return this.mnOctagonalBody
  }
}
